import { ProductTypes, addToBasket } from '@virginexperiencedays/basket';
import type { ApiItem as ApiBasketItem } from '@virginexperiencedays/basket';
import type { BasketItemFlags } from '@virginexperiencedays/basket/src/utils/basketItemFlags';
import { useQuickBasketContext } from '@virginexperiencedays/components-v2/src/drawers/QuickBasket/utils/QuickBasketContext';
import { BasketAddQuantity } from '@virginexperiencedays/components-v2/src/pages/product/AddToBasket/BasketAddQuantity';
import { useQty } from '@virginexperiencedays/components-v2/src/pages/product/AddToBasket/useQuantity';
import type { Product } from '@virginexperiencedays/products';
import { QUERY_STOCK } from '@virginexperiencedays/products/src/queries';
import { indexNames } from '@virginexperiencedays/search/constants';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import aa from 'search-insights';
import { gql, useQuery } from 'urql';
import { useAlgoliaQueryId, useAlgoliaUserToken } from '../../../../utils/algolia/tracking';
import { useHandleUpdateBasketCount } from '../../hooks/useHandleUpdateBasketCount';
import { calculateStockTime } from './calculateStockTime';
import { track } from './tracking';

export type AddToBasketProps = {
  className?: string;
  product: Product;
  withQty?: boolean;
  isActive: boolean;
  flags?: BasketItemFlags[];
  quickBasket?: boolean;
  handleQuickBasket?: (boolean: boolean) => void;
};

export function pauseFetching(): boolean {
  // pause fetching during build
  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
    return true;
  }
  return false;
}

const AddToBasket = ({
  className,
  product,
  withQty = true,
  isActive,
  flags,
  quickBasket,
  handleQuickBasket,
}: AddToBasketProps) => {
  const { handleAddToBasket, increment, decrement, qty, outOfStock, error } = useAddToBasket({
    product,
    flags,
    quickBasket,
    handleQuickBasket,
  });

  return (
    <BasketAddQuantity
      className={className}
      withQty={withQty}
      addToBasket={handleAddToBasket}
      increment={increment}
      decrement={decrement}
      qty={qty}
      outOfStock={outOfStock}
      error={error}
      isActive={isActive}
    />
  );
};

export const useAddToBasket = ({
  product,
  flags,
  quickBasket,
  handleQuickBasket,
}: Pick<AddToBasketProps, 'product' | 'flags' | 'quickBasket' | 'handleQuickBasket'>) => {
  const handleUpdateBasketCount = useHandleUpdateBasketCount();

  const { uuid } = useAlgoliaUserToken();
  const { queryId } = useAlgoliaQueryId();
  const { handleSetBasketItems, handleSetError, handleSetIsLoading, handleSetActivePromoCode } =
    useQuickBasketContext();

  const [error, setError] = useState(false);
  const [stateStock, setStateStock] = useState(null);
  const { increment, decrement, qty } = useQty();

  const [{ fetching, data }, reexecuteQuery] = useQuery({
    query: gql(QUERY_STOCK),
    variables: { slugs: [product?.slug] },
    pause: pauseFetching(),
    // don't use the cache
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    // stop if currently fetching
    if (fetching) return;

    const stock = data?.products?.[0]?.stock;
    setStateStock(stock);
    const refetchTimeout = setTimeout(() => {
      reexecuteQuery();
    }, calculateStockTime(stock));
    return () => clearTimeout(refetchTimeout);
  }, [data, fetching, reexecuteQuery]);

  const handleAddToBasket = async (): Promise<boolean> => {
    let result = false;

    try {
      if (queryId) {
        aa('convertedObjectIDsAfterSearch', {
          index: indexNames.DEFAULT,
          queryID: queryId,
          eventName: '[INSIGHTS] Product Added to Cart after Search',
          userToken: uuid,
          objectIDs: [`${product.id}`],
        });
      }

      handleSetIsLoading(true);

      const basket = await addToBasketWithQty({ qty, product, flags, setError, quickBasket });

      if (basket && Array.isArray(basket.items)) {
        result = true;
        const basketCount = basket.items.reduce((acc, item) => acc + (item.Qty || 0), 0);
        handleUpdateBasketCount?.(basketCount);
        handleSetBasketItems(basket.items);
        handleQuickBasket?.(true);
        basket.promoCode && handleSetActivePromoCode(basket.promoCode);
      } else {
        handleSetError('items is not an array');
      }
    } catch (error) {
      result = false;
      console.error(error);
    } finally {
      handleSetIsLoading(false);
    }

    return result;
  };

  // Previously it looked like this -> if (!product || !product.id || !product.slug) {
  // Logic is simplified, and there is no need to check for slug, as we wouldn't be able to render the page in general if there would be no slug given.
  if (!product?.id) {
    console.error(`AddToBasket: missing 'id'`);
    return null;
  }

  return {
    handleAddToBasket,
    increment,
    decrement,
    qty,
    outOfStock: stateStock <= 0 && stateStock !== null,
    error,
  };
};

async function addToBasketWithQty({
  product,
  qty,
  flags,
  setError,
  quickBasket,
}: {
  product: Product;
  qty: number;
  flags?: BasketItemFlags[];
  setError: Dispatch<SetStateAction<boolean>>;
  quickBasket?: boolean;
}): Promise<{ items: ApiBasketItem[]; promoCode: string | undefined } | undefined> {
  try {
    const basket = await addToBasket(product.id, qty, null, null, flags);

    if (!basket?.Items?.length) throw new Error('failed to add item(s) to basket');
    await trackAddToBasket({ product, qty, basket });

    if (!quickBasket) window.location.href = '/basket';
    else return { items: basket.Items as ApiBasketItem[], promoCode: basket.PromoCode };
  } catch (e) {
    console.error(e);
    setError(true);
  }
}

async function trackAddToBasket({
  product,
  qty,
  basket,
}: {
  product: Product;
  qty: number;
  basket: {
    BasketItemTotal?: number;
    Items?: {
      SKU: string;
      Qty: number;
      UnitPriceInclDiscount: number;
      Name: string;
      RRP: number;
      Subtotal: number;
      ProductUrl: string;
      ProductImageURL: string;
      Category: string;
      ProductTypeId: number;
    }[];
  };
}): Promise<void> {
  await track({
    totalAmount: basket?.BasketItemTotal ?? 0,
    product,
    qty,
    basketItems:
      basket?.Items?.map(
        ({
          SKU,
          Qty,
          UnitPriceInclDiscount,
          Name,
          RRP,
          Subtotal,
          ProductImageURL,
          Category,
          ProductTypeId,
          ProductUrl = '',
        }) => ({
          sku: SKU,
          qty: Qty,
          price: UnitPriceInclDiscount,
          slug: ProductUrl?.split('/product/')?.[1] ?? '',
          title: Name ?? '',
          rrp: RRP ?? 0,
          rowTotal: Subtotal ?? 0,
          url: ProductUrl ?? '',
          imageUrl: ProductImageURL ?? '',
          categories: Array.isArray(Category) ? Category : [String(Category)],
          isCollectionProduct: ProductTypeId === ProductTypes.Collection,
        }),
      ) ?? [],
  });
}

export default AddToBasket;
